/** @jsxFrag React.Fragment */
/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { FlexSpinner } from 'components'
import {
  useAcceptProvingChangesMutation,
  useRejectProvingChangesMutation,
} from 'components/EntityChangeList/hooks/ProvingChange.generated'
import { ProvingChangesList } from 'components/ProvingChangesList'
import { CurrentProvingContext } from 'context/CurrentProvingContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { PanelState, usePanelState } from 'hooks/usePanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import { ProvingChange, ProvingType } from 'model'
import {
  ActionButton,
  IPanelProps,
  IRenderFunction,
  Panel,
  PanelType,
  ScrollablePane,
  SelectionMode,
  Stack,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CancelFn } from 'router5/types/types/base'
import { useTheme } from 'theme'
import { PageDocuments } from './PageDocuments'
import { PageEditingHistory } from './PageEditingHistory'
import { PageFlows } from './PageFlows'
import { PageForms } from './PageForms'
import { PageGeneral, PageGeneralStatus } from './PageGeneral'
import { PageMails } from './PageMails'
import { PageMaterials } from './PageMaterials'
import { PageParticipants } from './PageParticipants'
import { PageResourceBookings } from './PageResourceBookings'
import { PageResourceRequirements } from './PageResourceRequirements'
import { PageSpecialNote } from './PageSpecialNote'
import { PageVehicles } from './PageVehicles'
import { ProvingHeader } from './ProvingHeader'
import { ProvingPagination } from './ProvingPagination'
import { useProvingQuery } from './hooks/index'

const PendingChangesPanelFooter: React.FC<{
  disabled?: boolean
  onAccept?: () => void
  onAcceptAll?: () => void
  onReject?: () => void
  onRejectAll?: () => void
}> = ({ onAccept, onAcceptAll, onReject, onRejectAll, disabled }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [disabledAll, setDisabledAll] = React.useState(false)

  return (
    <div>
      <ActionButton
        disabled={disabled}
        onClick={onAccept}
        iconProps={{
          iconName: 'CheckMark',
          style: { color: theme.palette.green },
        }}
      >
        {t('action.acceptSelection')}
      </ActionButton>
      <ActionButton
        disabled={disabled}
        onClick={onReject}
        iconProps={{
          iconName: 'Cancel',
          style: { color: theme.palette.red },
        }}
      >
        {t('action.rejectSelection')}
      </ActionButton>
      <ActionButton
        disabled={disabledAll}
        onClick={() => {
          if (onAcceptAll) {
            onAcceptAll()
            setDisabledAll(true)
          }
        }}
        iconProps={{
          iconName: 'CheckMark',
          style: { color: theme.palette.green },
        }}
      >
        {t('action.acceptAll')}
      </ActionButton>
      <ActionButton
        disabled={disabledAll}
        onClick={() => {
          if (onRejectAll) {
            onRejectAll()
            setDisabledAll(true)
          }
        }}
        iconProps={{
          iconName: 'Cancel',
          style: { color: theme.palette.red },
        }}
      >
        {t('action.rejectAll')}
      </ActionButton>
    </div>
  )
}

const ProvingChangesPanel: React.FC<{
  hidePendingChangesPanel: () => void
  updateParams: (
    updateParams: Partial<{
      page: string
    }>
  ) => CancelFn
  pendingChangesPanelState: Partial<PanelState>
}> = ({ hidePendingChangesPanel, updateParams, pendingChangesPanelState }) => {
  const [proving, refetch] = useCurrentProving()
  const { t } = useTranslation('ProvingDetailsView')
  const [changeListLoading, setChangeListLoading] = React.useState(false)

  const [acceptProvingChanges] = useAcceptProvingChangesMutation()
  const [rejectProvingChanges] = useRejectProvingChangesMutation()
  const [selection, selectedItems] = useSelection<ProvingChange>({
    getKey: (item) => item._id!,
  })
  const pendingChangesPanelFooterRenderer = React.useCallback<
    IRenderFunction<IPanelProps>
  >(
    () => (
      <PendingChangesPanelFooter
        disabled={selectedItems.length === 0}
        onAccept={async () => {
          setChangeListLoading(true)
          await acceptProvingChanges({
            variables: {
              changeIds: selectedItems.map((x) => x._id),
            },
          })
          await refetch()
          setChangeListLoading(false)
        }}
        onReject={async () => {
          setChangeListLoading(true)
          await rejectProvingChanges({
            variables: {
              changeIds: selectedItems.map((x) => x._id),
            },
          })
          await refetch()
          setChangeListLoading(false)
        }}
        onAcceptAll={async () => {
          const allItems = selection.getItems() as ProvingChange[]
          setChangeListLoading(true)
          await acceptProvingChanges({
            variables: {
              changeIds: allItems.map((x) => x._id),
            },
          })
          await refetch()
          setChangeListLoading(false)
        }}
        onRejectAll={async () => {
          const allItems = selection.getItems() as ProvingChange[]
          setChangeListLoading(true)
          await rejectProvingChanges({
            variables: {
              changeIds: allItems.map((x) => x._id),
            },
          })
          await refetch()
          setChangeListLoading(false)
        }}
      />
    ),
    [
      selection,
      selectedItems,
      acceptProvingChanges,
      rejectProvingChanges,
      refetch,
      setChangeListLoading,
    ]
  )

  return (
    <Panel
      type={PanelType.large}
      headerText={t('panels.pendingChanges.title')}
      closeButtonAriaLabel={t('action.close')}
      isLightDismiss={true}
      onRenderFooterContent={pendingChangesPanelFooterRenderer}
      {...pendingChangesPanelState}
    >
      <FlexSpinner loading={changeListLoading}>
        {proving && proving!.pendingChanges && (
          <ProvingChangesList
            provingChanges={proving!.pendingChanges}
            listProps={{
              selection,
              selectionMode: SelectionMode.multiple,
            }}
            onNavigateGroup={(group) => {
              updateParams({ page: group.name })
              hidePendingChangesPanel()
            }}
          />
        )}
      </FlexSpinner>
    </Panel>
  )
}

export const ProvingDetailsView: React.FunctionComponent = () => {
  // const { t } = useTranslation('ProvingDetailsView')
  const [params, updateParams, route] = useRouteParams(
    'provings.details',
    {
      page: 'general',
    },
    { persist: false }
  )

  const { data, loading, error, refetch } = useProvingQuery({
    variables: { id: route.params.id },
  })
  const proving = data?.proving

  const showVehiclesAndMaterials = proving
    ? proving.type !== ProvingType.BusinessTrip
    : false

  const handlePageSelected = React.useCallback(
    (page: string) => updateParams({ page }),
    [updateParams]
  )
  const [generalIsValid, setGeneralIsValid] = useState(
    PageGeneralStatus.CORRECT
  )
  // const [changeListLoading, setChangeListLoading] = React.useState(false)

  const [
    pendingChangesPanelState,
    showPendingChangesPanel,
    hidePendingChangesPanel,
  ] = usePanelState()
  // const [acceptProvingChanges] = useAcceptProvingChangesMutation()
  // const [rejectProvingChanges] = useRejectProvingChangesMutation()
  // const [selection, selectedItems] = useSelection<ProvingChange>({
  //   getKey: item => item._id!,
  // })
  // const pendingChangesPanelFooterRenderer = React.useCallback<
  //   IRenderFunction<IPanelProps>
  // >(
  //   () => (
  //     <PendingChangesPanelFooter
  //       disabled={selectedItems.length === 0}
  //       onAccept={async () => {
  //         setChangeListLoading(true)
  //         await acceptProvingChanges({
  //           variables: {
  //             changeIds: selectedItems.map(x => x._id),
  //           },
  //         })
  //         await refetch()
  //         setChangeListLoading(false)
  //       }}
  //       onReject={async () => {
  //         setChangeListLoading(true)
  //         await rejectProvingChanges({
  //           variables: {
  //             changeIds: selectedItems.map(x => x._id),
  //           },
  //         })
  //         await refetch()
  //         setChangeListLoading(false)
  //       }}
  //       onAcceptAll={async () => {
  //         const allItems = selection.getItems() as ProvingChange[]
  //         setChangeListLoading(true)
  //         await acceptProvingChanges({
  //           variables: {
  //             changeIds: allItems.map(x => x._id),
  //           },
  //         })
  //         await refetch()
  //         setChangeListLoading(false)
  //       }}
  //       onRejectAll={async () => {
  //         const allItems = selection.getItems() as ProvingChange[]
  //         setChangeListLoading(true)
  //         await rejectProvingChanges({
  //           variables: {
  //             changeIds: allItems.map(x => x._id),
  //           },
  //         })
  //         await refetch()
  //         setChangeListLoading(false)
  //       }}
  //     />
  //   ),
  //   [
  //     selection,
  //     selectedItems,
  //     acceptProvingChanges,
  //     rejectProvingChanges,
  //     refetch,
  //     setChangeListLoading,
  //   ]
  // )

  return proving === null ? (
    <div>Not Found!</div>
  ) : (
    <FlexSpinner loading={loading} error={error}>
      <CurrentProvingContext.Provider value={[proving!, refetch]}>
        <Stack verticalFill={true}>
          <Stack.Item>
            <ProvingPagination
              selectedPage={params.page}
              onSelectPage={handlePageSelected}
              generalIsValid={generalIsValid}
            />
            <Stack.Item>
              <ProvingHeader onShowPendingChanges={showPendingChangesPanel} />
            </Stack.Item>
          </Stack.Item>
          <Stack.Item grow={true} css={css({ position: 'relative' })}>
            <ScrollablePane>
              <>
                {params.page === 'general' && (
                  <PageGeneral
                    setPageToValid={setGeneralIsValid}
                    pageIsValid={generalIsValid}
                  />
                )}
                {params.page === 'participants' && <PageParticipants />}
                {params.page === 'vehicles' && showVehiclesAndMaterials && (
                  <PageVehicles />
                )}
                {params.page === 'materials' && showVehiclesAndMaterials && (
                  <PageMaterials />
                )}
                {params.page === 'resourceRequirements' && (
                  <PageResourceRequirements />
                )}
                {params.page === 'resourceBookings' && <PageResourceBookings />}
                {params.page === 'forms' && <PageForms />}
                {params.page === 'mails' && <PageMails />}
                {params.page === 'files' && <PageDocuments />}
                {params.page === 'workflow' && <PageFlows />}
                {params.page === 'specialNote' && <PageSpecialNote />}
                {params.page === 'editingHistory' && <PageEditingHistory />}
              </>
            </ScrollablePane>
          </Stack.Item>
        </Stack>
        <ProvingChangesPanel
          hidePendingChangesPanel={hidePendingChangesPanel}
          pendingChangesPanelState={pendingChangesPanelState}
          updateParams={updateParams}
        />
        {/* <Panel
          type={PanelType.large}
          headerText={t('panels.pendingChanges.title')}
          closeButtonAriaLabel={t('action.close')}
          isLightDismiss={true}
          onRenderFooterContent={pendingChangesPanelFooterRenderer}
          {...pendingChangesPanelState}
        > */}
        {/* <FlexSpinner loading={changeListLoading}>
            {proving && proving!.pendingChanges && (
              <ProvingChangesList
                provingChanges={proving!.pendingChanges}
                listProps={{
                  selection,
                  selectionMode: SelectionMode.multiple,
                }}
                onNavigateGroup={group => {
                  updateParams({ page: group.name })
                  hidePendingChangesPanel()
                }}
              />
            )}
          </FlexSpinner> */}
        {/* </Panel> */}
      </CurrentProvingContext.Provider>
    </FlexSpinner>
  )
}
